import { useState } from 'react';
import { isAxiosError } from 'axios';
import type { ImportColumns, ImportField, ImportPreview, ImportResult } from '../../../types/import';

export const NO_COLUMN = '';

interface UseExcelImportParams {
  companyId: number | null;
  fields: ImportField[];
  loadPreview: (file: File) => Promise<ImportPreview>;
  runImport: (companyId: number, file: File, columns: ImportColumns) => Promise<ImportResult>;
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err) && typeof err.response?.data === 'string' && err.response.data) {
    return err.response.data;
  }
  return fallback;
}

export function useExcelImport({
  companyId,
  fields,
  loadPreview,
  runImport,
}: UseExcelImportParams) {
  // State
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Column mapping: field key -> selected column index as string, NO_COLUMN when unmapped
  const [selectedColumns, setSelectedColumns] = useState<Record<string, string>>({});

  const missingRequiredField = fields.some(
    (field) => field.required && (selectedColumns[field.key] ?? NO_COLUMN) === NO_COLUMN,
  );

  const setColumn = (fieldKey: string, value: string) => {
    setSelectedColumns((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const toSuggestedSelection = (data: ImportPreview): Record<string, string> => (
    Object.fromEntries(
      fields.map((field) => [field.key, data.suggestedColumns[field.key]?.toString() ?? NO_COLUMN]),
    )
  );

  const toColumns = (): ImportColumns => (
    Object.fromEntries(
      fields.map((field) => {
        const value = selectedColumns[field.key] ?? NO_COLUMN;
        return [field.key, value === NO_COLUMN ? undefined : Number(value)];
      }),
    )
  );

  const handleFileSelected = async (selected: File) => {
    setFile(selected);
    setPreview(null);
    setResult(null);
    setError(null);
    setLoading(true);

    try {
      const data = await loadPreview(selected);
      setPreview(data);
      setSelectedColumns(toSuggestedSelection(data));
    } catch (err) {
      console.error('Error reading import file:', err);
      setFile(null);
      setError(extractErrorMessage(err, 'Не вдалося прочитати файл. Перевірте, що це .xlsx.'));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!companyId || !file || missingRequiredField) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const importResult = await runImport(companyId, file, toColumns());
      setResult(importResult);
      setPreview(null);
      setFile(null);
    } catch (err) {
      console.error('Error importing file:', err);
      setError(extractErrorMessage(err, 'Не вдалося виконати імпорт. Спробуйте ще раз.'));
    } finally {
      setLoading(false);
    }
  };

  const handleImportAnother = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setSelectedColumns({});
  };

  return {
    data: {
      file,
      preview,
      result,
      loading,
      error,
    },
    mapping: {
      selectedColumns,
      missingRequiredField,
      setColumn,
    },
    actions: {
      handleFileSelected,
      handleImport,
      handleImportAnother,
    },
  };
}
